import React from 'react';
import { MapPin, Phone, Mail, Calendar, ChevronRight, Users } from '../lib/icons';
import { Doctor, ProviderType } from '../lib/supabase';
import { getProviderTypeLabel } from '../utils/providerTypeUtils';
import { getProfileImage } from '../utils/imageUtils';
import { useProviderImages } from '../hooks/useProviderImages';
import RatingDisplay from './RatingDisplay';
import DoctorSpecialtiesDisplay from './DoctorSpecialtiesDisplay';

interface DoctorCardProps {
  doctor: Doctor;
  onDoctorClick: (doctorId: string) => void;
  fromSearch?: boolean;
}

const DoctorCard: React.FC<DoctorCardProps> = ({
  doctor,
  onDoctorClick,
  fromSearch = false
}) => {
  const { providerImages } = useProviderImages(doctor.id);

  const getTypeIcon = (type: ProviderType) => {
    // This function could be moved to utils if needed elsewhere
    const iconMap = {
      'doctor': '🩺',
      'group_practice': '👥',
      'clinic': '🏥',
      'naturopath': '🌿',
      'psychotherapist': '🧠',
      'therapy_practice': '💪',
      'ambulanz': '🚑',
      'fachklinik': '⚕️',
      'labor_diagnostik': '🔬',
      'mvz': '🛡️',
      'naturheilpraxis': '❤️',
      'pflegedienst': '♿',
      'reha': '⚡',
      'dentist': '😊',
      'sonstiges': '👁️'
    };
    return iconMap[type] || '🩺';
  };

  return (
    <div
      className="bg-white rounded-lg shadow-md p-4 sm:p-6 hover:shadow-lg transition-shadow cursor-pointer border border-gray-200"
      onClick={() => onDoctorClick(doctor.id)}
    >
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
        {/* Doctor Image */}
        <div className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 lg:w-48 lg:h-48 flex-shrink-0">
          <img
            src={getProfileImage(doctor.image_url, providerImages)}
            alt={`${doctor.first_name} ${doctor.last_name}`}
            className="w-full h-full object-cover rounded-lg transition-transform duration-300 hover:scale-105"
            loading="lazy"
            decoding="async"
            fetchpriority={fromSearch ? 'low' : 'auto'}
            width={400}
            height={400}
          />
        </div>

        {/* Doctor Info */}
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 gap-2">
            <div className="flex-1 min-w-0">
              <div className="mb-2">
                <div className="flex items-start gap-2 mb-2">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 flex-1 min-w-0">
                    {doctor.provider_name}
                  </h3>
                  {doctor.is_premium && (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full whitespace-nowrap flex-shrink-0">
                      Premium
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full flex items-center whitespace-nowrap">
                    <span className="mr-1">{getTypeIcon(doctor.type)}</span>
                    {getProviderTypeLabel(doctor.type, doctor.salutation)}
                  </span>
                </div>
              </div>

              {/* Fachgebiete unter dem Namen */}
              <div className="mb-3">
                <DoctorSpecialtiesDisplay doctorId={doctor.id} />
              </div>

              <div className="mb-2">
                <RatingDisplay doctorId={doctor.id} className="text-sm" />
              </div>
            </div>
          </div>

          {/* Address with Distance */}
          <div className="flex items-center text-gray-600 mb-3">
            <MapPin className="w-4 h-4 mr-1" />
            <span className="text-sm break-words">{doctor.city}</span>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            {doctor.profile_phone && (
              <a
                href={`tel:${doctor.profile_phone}`}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center justify-center px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                aria-label={`${doctor.provider_name} anrufen unter ${doctor.profile_phone}`}
              >
                <Phone className="w-4 h-4 mr-2" />
                <span>Anrufen</span>
              </a>
            )}
            {doctor.profile_email && doctor.show_email && (
              <a
                href={`mailto:${doctor.profile_email}`}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center justify-center px-3 sm:px-4 py-2 hover:bg-green-800 transition-colors text-sm"
                aria-label={`E-Mail an ${doctor.provider_name} senden`}
              >
                <Mail className="w-4 h-4 mr-2" />
                <span>E-Mail</span>
              </a>
            )}
            {doctor.online_booking_enabled && doctor.online_booking_url && (
              <a
                href={doctor.online_booking_url.startsWith('http://') || doctor.online_booking_url.startsWith('https://') ? doctor.online_booking_url : `https://${doctor.online_booking_url}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center justify-center px-2 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-xs font-medium"
                aria-label={`Online-Termin bei ${doctor.provider_name} buchen`}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Online Termin
              </a>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDoctorClick(doctor.id);
              }}
              className="flex items-center justify-center px-3 sm:px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
              aria-label={`Details zu ${doctor.provider_name} anzeigen`}
            >
              <span>Details</span>
              <ChevronRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorCard;